import { useEffect, type RefObject } from "react";

/**
 * The dismiss contract every menu trigger owes its user: outside-click closes, Escape closes and returns
 * focus to the trigger, and focus moves to the first item on open.
 *
 * It lives here because it is invisible when right and a keyboard trap when wrong, and because two call
 * sites had already hand-rolled the same ~20 lines before it was extracted (2026-08-23, from Booking's
 * "+ Add show" and the show editor's Save; the overflow menu in the show preview would have been a third).
 *
 * **Escape closes the MENU only.** The listener runs in the CAPTURE phase and stops propagation, so a menu
 * inside a dialog does not close the dialog on the first Escape — the overlay stack unwinds one layer at a
 * time, which is the rule `Modal`/`AcDialog` already own.
 *
 * **Not the same thing as `useDismiss`**, and don't collapse the two: that one is the LIGHT dismiss for a
 * popover (`pointerdown`, plain Escape, no focus moves). A menu's Escape has to be captured and stopped or
 * every menu inside a dialog takes the dialog down with it, and a menu owes its keyboard user the focus
 * move that this hook makes.
 */
export function useMenuDismiss(
  open: boolean,
  close: () => void,
  rootRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, close, rootRef, triggerRef]);
}
